<template>
  <div class="container-fluid mt-4 px-4">
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
      <div>
        <h3 class="fw-bold text-dark mb-1">Trade History</h3>
        <p class="text-muted mb-0">Overview of your recent trading activity</p>
      </div>

      <div class="d-flex align-items-center gap-3">
        <div class="input-group" style="max-width: 300px;">
          <span class="input-group-text bg-white border-end-0 text-muted">
            <i class="fas fa-search"></i>
          </span>
          <input
            type="text"
            class="form-control border-start-0 ps-0 shadow-none"
            placeholder="Search trades..."
            v-model="searchQuery"
            @input="handleSearch"
          >
          <button v-if="searchQuery" class="btn bg-white border border-start-0 text-muted" type="button" @click="clearSearch">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="d-flex align-items-center gap-3">
          <div class="d-flex align-items-center gap-2">
            <label class="text-muted small mb-0">Show:</label>
            <select class="form-select form-select-sm" style="width: 70px;" v-model="pageSize" @change="currentPage = 1; fetchTradeHistory(1)">
              <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
            </select>
          </div>
          <span class="badge bg-light text-dark border py-2 px-3">
            Total: {{ totalTrades }}
          </span>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="d-flex justify-content-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger shadow-sm border-0" role="alert">
      <i class="fas fa-exclamation-circle me-2"></i> {{ error }}
    </div>

    <div v-else class="card shadow-sm border-0 rounded-3">
      <div class="card-body p-0">
        <div class="table-responsive table-scroll">
          <table class="table table-hover align-middle mb-0">
            <thead class="bg-light text-secondary sticky-header">
              <tr>
                <th class="py-3 ps-3" style="width: 30px;"></th>
                <th class="ps-4 py-3 fw-semibold text-uppercase small cursor-pointer user-select-none" @click="handleSort('timestamp', $event)">
                  Date & Time
                  <span class="ms-1" v-html="getSortIcon('timestamp')"></span>
                </th>
                <th class="py-3 fw-semibold text-uppercase small cursor-pointer user-select-none" @click="handleSort('ticker', $event)">
                  Ticker
                  <span class="ms-1" v-html="getSortIcon('ticker')"></span>
                </th>
                <th class="py-3 fw-semibold text-uppercase small text-muted">
                  Company
                </th>
                <th class="py-3 fw-semibold text-uppercase small cursor-pointer user-select-none" @click="handleSort('side', $event)">
                  Type
                  <span class="ms-1" v-html="getSortIcon('side')"></span>
                </th>
                <th class="py-3 fw-semibold text-uppercase small cursor-pointer user-select-none" @click="handleSort('position_side', $event)">
                  Side
                  <span class="ms-1" v-html="getSortIcon('position_side')"></span>
                </th>
                <th class="py-3 fw-semibold text-uppercase small cursor-pointer user-select-none" @click="handleSort('qty', $event)">
                  Shares
                  <span class="ms-1" v-html="getSortIcon('qty')"></span>
                </th>
                <th class="py-3 fw-semibold text-uppercase small cursor-pointer user-select-none" @click="handleSort('price', $event)">
                  Price
                  <span class="ms-1" v-html="getSortIcon('price')"></span>
                </th>
                <th class="py-3 fw-semibold text-uppercase small text-muted cursor-not-allowed" title="Sorting by calculated value not supported">
                  Total Value
                </th>
                <th class="py-3 fw-semibold text-uppercase small text-end cursor-pointer user-select-none" @click="handleSort('status', $event)">
                  <span class="me-1" v-html="getSortIcon('status')"></span>
                  Status
                </th>
                <th class="py-3 fw-semibold text-uppercase small text-end pe-4 text-muted">
                  Trace ID
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="trade in trades" :key="trade.id">
                <tr>
                  <td class="ps-3 text-center">
                    <span
                      class="expand-toggle text-muted"
                      role="button"
                      @click="toggleTickerExpansion(trade.ticker)"
                      :title="isTickerExpanded(trade.ticker) ? `Hide trades for ${trade.ticker}` : `Show all trades for ${trade.ticker}`"
                    >
                      <i :class="['fas', isTickerExpanded(trade.ticker) ? 'fa-chevron-down' : 'fa-chevron-right']" style="font-size: 0.75rem;"></i>
                    </span>
                  </td>
                  <td class="ps-4 text-muted">
                    <i class="far fa-clock me-2 text-secondary opacity-50"></i>
                    {{ formatDateTime(trade.timestamp) }}
                  </td>
                  <td>
                    <a :href="`https://finance.yahoo.com/quote/${trade.ticker}`" target="_blank" rel="noopener noreferrer" class="text-primary text-decoration-none fw-bold">
                      {{ trade.ticker }} <i class="fas fa-external-link-alt small ms-1" style="font-size: 0.7em;"></i>
                    </a>
                    <span class="ms-2">
                      <TickerActionButtons :ticker="trade.ticker" />
                    </span>
                  </td>
                  <td class="text-muted small">
                    {{ companyNames[trade.ticker] || '-' }}
                  </td>
                  <td>
                    <span :class="getTypeBadgeClass(trade)">
                      {{ trade.side }}
                    </span>
                  </td>
                  <td>
                    <span :class="getSideBadgeClass(trade)">
                      {{ (trade.position_side || 'LONG').toUpperCase() }}
                    </span>
                  </td>
                  <td class="text-dark">{{ trade.qty }}</td>
                  <td class="text-dark">
                    <span v-if="isPricePending(trade)" class="text-muted fst-italic">Pending</span>
                    <span v-else>${{ trade.price.toFixed(2) }}</span>
                  </td>
                  <td class="fw-bold text-dark">
                    <span v-if="isPricePending(trade)">—</span>
                    <span v-else>${{ (trade.qty * trade.price).toFixed(2) }}</span>
                    <span v-if="trade.side === 'SELL' && !isPricePending(trade)" class="ms-2 d-inline-block">
                      <span v-if="fetchingPnl.has(trade.id)" class="spinner-border spinner-border-sm text-secondary" role="status" style="width: 1rem; height: 1rem;">
                        <span class="visually-hidden">Loading...</span>
                      </span>
                      <template v-else-if="realizedPnl[trade.id] !== undefined && realizedPnl[trade.id] !== null">
                        <span v-if="realizedPnl[trade.id] === 0" class="badge rounded-pill bg-secondary-subtle text-secondary">$0.00</span>
                        <span v-else :class="['badge rounded-pill', realizedPnl[trade.id] > 0 ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger']">
                          {{ realizedPnl[trade.id] > 0 ? '+' : '-' }}${{ Math.abs(realizedPnl[trade.id]).toFixed(2) }}
                        </span>
                      </template>
                    </span>
                  </td>
                  <td class="text-end">
                    <span class="badge bg-secondary-subtle text-secondary border border-secondary-subtle">
                      {{ trade.status }}
                    </span>
                  </td>
                  <td class="text-end pe-4 text-muted font-monospace small" :title="trade.e2etransactionid">
                    {{ trade.e2etransactionid ? trade.e2etransactionid.substring(0, 8) + '...' : '-' }}
                  </td>
                </tr>
                <tr v-if="isTickerExpanded(trade.ticker)" :key="`${trade.id}-ticker-trades`" class="ticker-trades-row">
                  <td colspan="11" class="p-0">
                    <div class="px-3 px-md-4 py-3 bg-light border-bottom">
                      <p class="text-muted small fw-semibold mb-2 text-uppercase" style="font-size: 0.7rem; letter-spacing: 0.5px;">
                        Trade History - {{ trade.ticker }}
                      </p>
                      <div v-if="loadingTickerTrades[trade.ticker]" class="d-flex align-items-center gap-2 text-muted small">
                        <span class="spinner-border spinner-border-sm" role="status" style="width: 0.9rem; height: 0.9rem;"></span>
                        Loading trades for {{ trade.ticker }}...
                      </div>
                      <div v-else-if="tickerTrades[trade.ticker] === null" class="text-danger small">
                        <i class="fas fa-exclamation-circle me-1"></i> Failed to load trades.
                      </div>
                      <div v-else-if="tickerTrades[trade.ticker] && tickerTrades[trade.ticker].length === 0" class="text-muted small">
                        <i class="fas fa-info-circle me-1"></i> No trades found for {{ trade.ticker }}.
                      </div>
                      <div v-else-if="tickerTrades[trade.ticker] && tickerTrades[trade.ticker].length > 0" class="table-responsive">
                        <table class="table table-sm align-middle mb-0 border rounded bg-white">
                          <thead class="table-light text-secondary">
                            <tr>
                              <th class="py-2 ps-3 fw-semibold text-uppercase small">Date & Time</th>
                              <th class="py-2 fw-semibold text-uppercase small">Type</th>
                              <th class="py-2 text-end fw-semibold text-uppercase small">Shares</th>
                              <th class="py-2 text-end fw-semibold text-uppercase small">Price</th>
                              <th class="py-2 text-end fw-semibold text-uppercase small">Total</th>
                              <th class="py-2 text-end pe-3 fw-semibold text-uppercase small">Trace ID</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="t in tickerTrades[trade.ticker]" :key="t.id">
                              <td class="ps-3 text-muted small">
                                <i class="far fa-clock me-1 text-secondary opacity-50"></i>
                                {{ formatDateTime(t.timestamp) }}
                              </td>
                              <td>
                                <span :class="['badge rounded-pill px-3', t.side === 'BUY' ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger']">
                                  {{ t.side }}
                                </span>
                              </td>
                              <td class="text-end text-dark small">{{ t.qty }}</td>
                              <td class="text-end text-dark small">
                                <span v-if="isPricePending(t)" class="text-muted fst-italic">Pending</span>
                                <span v-else>${{ t.price.toFixed(2) }}</span>
                              </td>
                              <td class="text-end fw-bold text-dark small">
                                <span v-if="isPricePending(t)">—</span>
                                <span v-else>${{ (t.qty * t.price).toFixed(2) }}</span>
                              </td>
                              <td class="text-end pe-3 text-muted font-monospace small" :title="t.e2etransactionid">
                                {{ t.e2etransactionid ? t.e2etransactionid.substring(0, 8) + '...' : '-' }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
              <tr v-if="trades.length === 0">
                <td colspan="11" class="text-center py-5 text-muted">
                  <i class="fas fa-history fa-2x mb-3 opacity-25"></i>
                  <p class="mb-0">No trading history found.</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div class="card-footer bg-white border-top-0 py-3 d-flex justify-content-between align-items-center" v-if="trades.length > 0">
        <span class="text-muted small">
          Showing page <strong>{{ currentPage }}</strong> of <strong>{{ totalPages }}</strong>
        </span>
        <nav aria-label="Page navigation">
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <a class="page-link border-0 text-secondary" href="#" @click.prevent="prevPage">
                <i class="fas fa-chevron-left"></i> Prev
              </a>
            </li>
            <li v-for="page in totalPages" :key="page" class="page-item" :class="{ active: currentPage === page }">
              <a class="page-link border-0 rounded-circle mx-1" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <a class="page-link border-0 text-secondary" href="#" @click.prevent="nextPage">
                Next <i class="fas fa-chevron-right"></i>
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchTrades, validateTicker, fetchRealizedPnlForTicker, fetchRealizedPnlByTransaction } from '../services/api';
import TickerActionButtons from '../components/TickerActionButtons.vue';

export default {
  name: 'TradeHistory',
  components: { TickerActionButtons },
  data() {
    return {
      trades: [],
      currentPage: 1,
      totalPages: 1,
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100],
      totalTrades: 0,
      isLoading: false,
      error: null,
      searchQuery: '',
      searchTimeout: null,
      currentSort: [], // Array of { key: 'column', order: 'asc'|'desc' }
      companyNames: {},
      fetchingNames: new Set(),
      realizedPnl: {},
      fetchingPnl: new Set(),
      expandedTickers: {},
      tickerTrades: {},
      loadingTickerTrades: {}
    }
  },
  methods: {
    async fetchTradeHistory(page) {
      this.isLoading = true;
      this.error = null;
      try {
        const data = await fetchTrades(page, this.pageSize, this.searchQuery, this.currentSort);
        if (data) {
            this.trades = data.trades || [];
            this.totalTrades = data.total_trades || 0;
            this.totalPages = Math.ceil(this.totalTrades / this.pageSize) || 1;
            this.currentPage = data.page || 1;

            // Fetch company names for displayed trades
            const uniqueTickers = [...new Set(this.trades.map(t => t.ticker))];
            uniqueTickers.forEach(ticker => this.fetchCompanyName(ticker));

            // Fetch realized PnL per sell trade, keyed by trade.id to avoid ticker collisions
            this.trades.filter(t => t.side === 'SELL').forEach(trade => this.fetchRealizedPnlForTrade(trade));
        }
      } catch (err) {
        this.error = "Failed to load trade history. Please check your connection.";
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchCompanyName(symbol) {
      if (this.companyNames[symbol] || this.fetchingNames.has(symbol)) return;
      this.fetchingNames.add(symbol);
      try {
        const data = await validateTicker(symbol);
        if (data && data.valid && data.name) {
          this.companyNames[symbol] = data.name;
        }
      } catch (error) {
        console.error(`Error fetching company name for ${symbol}:`, error);
      } finally {
        this.fetchingNames.delete(symbol);
      }
    },
    // @deprecated — use fetchRealizedPnlForTrade instead; kept for backward compatibility
    async fetchRealizedPnlForTickerMethod(ticker) {
      if (this.realizedPnl[ticker] !== undefined || this.fetchingPnl.has(ticker)) return;
      this.fetchingPnl.add(ticker);
      try {
        const data = await fetchRealizedPnlForTicker(ticker);
        if (data) {
          this.realizedPnl[ticker] = data.realized_pnl;
        }
      } catch (error) {
        console.error(`Error fetching realized PnL for ${ticker}:`, error);
        this.realizedPnl[ticker] = null;
      } finally {
        this.fetchingPnl.delete(ticker);
      }
    },
    async fetchRealizedPnlForTrade(trade) {
      if (this.realizedPnl[trade.id] !== undefined || this.fetchingPnl.has(trade.id)) return;
      if (!trade.e2etransactionid) {
        console.warn(`[TradeHistory] skipping PnL fetch — e2etransactionid missing for trade.id=${trade.id}, ticker=${trade.ticker}`);
        this.realizedPnl[trade.id] = null;
        return;
      }
      this.fetchingPnl.add(trade.id);
      console.info(`[TradeHistory] PnL fetch started: trade.id=${trade.id}, e2etransactionid=${trade.e2etransactionid}`);
      try {
        const data = await fetchRealizedPnlByTransaction(trade.e2etransactionid);
        this.realizedPnl[trade.id] = data ? data.realized_pnl : null;
        console.info(`[TradeHistory] PnL fetch succeeded: trade.id=${trade.id}, realized_pnl=${this.realizedPnl[trade.id]}`);
      } catch (error) {
        console.info(`[TradeHistory] PnL fetch failed: trade.id=${trade.id}, error=${error}`);
        this.realizedPnl[trade.id] = null;
      } finally {
        this.fetchingPnl.delete(trade.id);
      }
    },
    formatDateTime(timestamp) {
      if (!timestamp) return '-';
      let ts = timestamp;
      if (typeof ts === 'string' && !ts.endsWith('Z') && ts.includes('T')) {
        ts += 'Z';
      }
      const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
      return new Date(ts).toLocaleString(undefined, options);
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.fetchTradeHistory(this.currentPage + 1);
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.fetchTradeHistory(this.currentPage - 1);
      }
    },
    goToPage(page) {
      this.fetchTradeHistory(page);
    },
    handleSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchTradeHistory(1);
      }, 500); // Debounce search
    },
    clearSearch() {
      this.searchQuery = '';
      this.handleSearch();
    },
    handleSort(key, event) {
      // Check if Shift key is pressed for multi-sort
      const isMultiSort = event.shiftKey;

      const existingSortIndex = this.currentSort.findIndex(s => s.key === key);

      if (existingSortIndex > -1) {
        // Already sorting by this key
        const existingSort = this.currentSort[existingSortIndex];
        if (existingSort.order === 'asc') {
          // Toggle to desc
          existingSort.order = 'desc';
        } else {
          // Remove sort
          this.currentSort.splice(existingSortIndex, 1);
        }
      } else {
        // New sort key
        if (!isMultiSort) {
          // If not multi-sort, clear other sorts
          this.currentSort = [];
        }
        this.currentSort.push({ key, order: 'asc' });
      }

      this.currentPage = 1;
      this.fetchTradeHistory(1);
    },
    getSortIcon(key) {
      const sort = this.currentSort.find(s => s.key === key);
      if (!sort) return '<i class="fas fa-sort text-muted opacity-25"></i>';
      return sort.order === 'asc'
        ? '<i class="fas fa-sort-up text-primary"></i>'
        : '<i class="fas fa-sort-down text-primary"></i>';
    },
    isTickerExpanded(ticker) {
      return !!this.expandedTickers[ticker];
    },
    toggleTickerExpansion(ticker) {
      if (this.expandedTickers[ticker]) {
        this.expandedTickers[ticker] = false;
        console.debug(`[TradeHistory] Ticker collapsed: ${ticker}`);
        return;
      }
      this.expandedTickers[ticker] = true;
      console.debug(`[TradeHistory] Ticker expanded: ${ticker}`);
      if (this.tickerTrades[ticker] === undefined) {
        this.loadTradesForTicker(ticker);
      }
    },
    async loadTradesForTicker(ticker) {
      this.loadingTickerTrades[ticker] = true;
      try {
        const data = await fetchTrades(1, 100, ticker);
        const allTrades = (data && data.trades) || [];
        this.tickerTrades[ticker] = allTrades.filter(t => t.ticker === ticker);
        console.info(`[TradeHistory] Loaded ${this.tickerTrades[ticker].length} trade(s) for ${ticker}`);
      } catch (error) {
        console.error(`[TradeHistory] Failed to fetch trades for ${ticker}:`, error);
        this.tickerTrades[ticker] = null;
      } finally {
        this.loadingTickerTrades[ticker] = false;
      }
    },
    isPricePending(trade) {
      return !trade.price || trade.price === 0;
    },
    // A trade is terminal under either status: 'Completed' from the simulator
    // stored procedures, 'Filled' from the Alpaca fill path. Accepting only
    // 'Completed' greyed out every broker-executed trade in live/paper mode.
    isTradeValid(trade) {
      const valid = trade.status === 'Completed' || trade.status === 'Filled';
      console.debug(`[TradeHistory] isTradeValid: id=${trade.id}, status="${trade.status}", valid=${valid}`);
      return valid;
    },
    getTypeBadgeClass(trade) {
      if (!this.isTradeValid(trade)) {
        return 'badge rounded-pill px-3 bg-secondary-subtle text-secondary';
      }
      return trade.side === 'BUY'
        ? 'badge rounded-pill px-3 bg-success-subtle text-success'
        : 'badge rounded-pill px-3 bg-danger-subtle text-danger';
    },
    getSideBadgeClass(trade) {
      // LONG → blue, SHORT → amber. Side is the position direction, not the
      // broker action (BUY/SELL), so it is independent of `trade.side`.
      const sidePosition = (trade.position_side || 'LONG').toUpperCase();
      return sidePosition === 'SHORT'
        ? 'badge rounded-pill px-3 bg-warning-subtle text-warning border border-warning-subtle'
        : 'badge rounded-pill px-3 bg-primary-subtle text-primary border border-primary-subtle';
    },
  },
  mounted() {
    this.fetchTradeHistory(this.currentPage).then(() => {
      const buys = this.trades.filter(t => t.side === 'BUY').length;
      const sells = this.trades.filter(t => t.side === 'SELL').length;
      const invalids = this.trades.filter(t => !this.isTradeValid(t)).length;
      console.debug(`[TradeHistory] Color coding active — ${this.trades.length} trade(s) rendered: ${buys} buy(s) (green), ${sells} sell(s) (red), ${invalids} invalid(s) (gray)`);
    });
  }
}
</script>

<style scoped>
/* Modern styling enhancements */
.bg-light {
  background-color: #f8f9fa !important;
}

/* Softer badges using subtle backgrounds */
.bg-success-subtle {
  background-color: #d1e7dd !important;
  color: #0f5132 !important;
}
.bg-danger-subtle {
  background-color: #f8d7da !important;
  color: #842029 !important;
}
.bg-secondary-subtle {
  background-color: #e2e3e5 !important;
  color: #41464b !important;
}

/* Pagination overrides */
.pagination .page-link {
  color: #6c757d;
  min-width: 32px;
  text-align: center;
}
.pagination .page-item.active .page-link {
  background-color: #0d6efd;
  color: white;
  font-weight: 600;
}
.pagination .page-link:hover {
  background-color: #e9ecef;
  color: #0d6efd;
}
.pagination .page-item.disabled .page-link {
  color: #adb5bd;
  background-color: transparent;
}

/* Typography refinements */
th {
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}
.cursor-pointer {
  cursor: pointer;
}
.cursor-not-allowed {
  cursor: not-allowed;
}
.user-select-none {
  user-select: none;
}
td {
  font-size: 0.9rem;
}
.card {
  overflow: hidden; /* Ensures rounded corners clip the table */
}

/* Sticky table header */
.table-scroll {
  max-height: 70vh;
  overflow-y: auto;
}
.sticky-header th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #f8f9fa;
}

/* Expandable per-ticker row */
.expand-toggle {
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
}
.expand-toggle:hover {
  opacity: 1;
}
.ticker-trades-row td {
  border-top: none;
}
</style>